<template>
  <div class="page-card">
    <div class="page-head">
      <h3>Menu Management</h3>
      <el-button type="primary" size="small" @click="openCreate()">New Menu</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Menu Name">
        <el-input v-model="query.menuName" clearable placeholder="Please enter a menu name" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="Status">
        <el-select v-model="query.status" clearable placeholder="All">
          <el-option label="Active" value="0" />
          <el-option label="Disabled" value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table
      :data="list"
      v-loading="loading"
      row-key="menuId"
      border
      default-expand-all
      size="small"
      :tree-props="{ children: 'children' }"
    >
      <el-table-column prop="menuName" label="Menu Name" min-width="240">
        <template slot-scope="scope">
          <i :class="iconClass(scope.row)" class="menu-icon"></i>
          <span>{{ scope.row.menuName }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="menuType" label="Type" width="110">
        <template slot-scope="scope">
          <el-tag :type="typeTag(scope.row.menuType)" size="small">{{ typeText(scope.row.menuType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="orderNum" label="Sort" width="130">
        <template slot-scope="scope">
          <el-input-number
            v-model="scope.row.orderNum"
            size="mini"
            :min="0"
            controls-position="right"
            @change="quickSaveOrder(scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="perms" label="Permission Key" min-width="180" show-overflow-tooltip />
      <el-table-column prop="component" label="Component Path" min-width="220" show-overflow-tooltip />
      <el-table-column prop="path" label="Route Path" min-width="150" show-overflow-tooltip />
      <el-table-column prop="status" label="Status" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === '0' ? '' : 'info'" size="small">
            {{ scope.row.status === '0' ? 'Active' : 'Disabled' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="210" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button type="text" size="small" @click="openCreate(scope.row)">New</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="640px">
      <el-form :model="form" label-width="100px" size="small">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Parent Menu">
              <el-select v-model="form.parentId" filterable style="width:100%">
                <el-option label="Root" :value="0" />
                <el-option
                  v-for="item in parentOptions"
                  :key="item.menuId"
                  :label="item.label"
                  :value="item.menuId"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Menu Type">
              <el-radio-group v-model="form.menuType">
                <el-radio label="M">Directory</el-radio>
                <el-radio label="C">Menu</el-radio>
                <el-radio label="F">Button</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item label="Menu Name"><el-input v-model="form.menuName" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Sort Order"><el-input-number v-model="form.orderNum" :min="0" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Route Path"><el-input v-model="form.path" placeholder="/system or user" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Component"><el-input v-model="form.component" placeholder="system/user/index" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Permission Key"><el-input v-model="form.perms" placeholder="system:user:list" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Icon"><el-input v-model="form.icon" placeholder="el-icon-menu" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="Status">
              <el-radio-group v-model="form.status">
                <el-radio label="0">Active</el-radio>
                <el-radio label="1">Disabled</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { menuApi } from '@/api/system'

const emptyForm = () => ({
  menuId: null,
  parentId: 0,
  menuName: '',
  path: '',
  component: '',
  perms: '',
  menuType: 'M',
  icon: '',
  orderNum: 0,
  status: '0'
})

const emptyQuery = () => ({ menuName: '', status: '' })

export default {
  data() {
    return {
      loading: false,
      list: [],
      query: emptyQuery(),
      dialogVisible: false,
      dialogTitle: 'New Menu',
      form: emptyForm()
    }
  },
  computed: {
    parentOptions() {
      const options = []
      const walk = (nodes, level) => {
        nodes.forEach(node => {
          if (node.menuId !== this.form.menuId) {
            options.push({ menuId: node.menuId, label: `${'　'.repeat(level)}${node.menuName}` })
          }
          if (node.children && node.children.length) walk(node.children, level + 1)
        })
      }
      walk(this.list, 0)
      return options
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await menuApi.list(this.query)
        this.list = res.data || []
      } finally {
        this.loading = false
      }
    },
    search() {
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    openCreate(parent) {
      this.dialogTitle = 'New Menu'
      this.form = emptyForm()
      if (parent) {
        this.form.parentId = parent.menuId
        this.form.menuType = parent.menuType === 'M' ? 'C' : 'F'
      }
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = 'Edit Menu'
      this.form = { ...emptyForm(), ...this.cleanPayload(row) }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.menuName) {
        this.$message.warning('Please enter a menu name')
        return
      }
      if (this.form.menuId) await menuApi.update(this.cleanPayload(this.form))
      else await menuApi.create(this.cleanPayload(this.form))
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    async quickSaveOrder(row) {
      await menuApi.update(this.cleanPayload(row))
      this.$message.success('Sort order saved')
    },
    async remove(row) {
      await this.$confirm(`Delete menu ${row.menuName}?`, 'Confirm', { type: 'warning' })
      await menuApi.remove(row.menuId)
      this.$message.success('Deleted successfully')
      this.loadData()
    },
    cleanPayload(row) {
      const { children, ...payload } = row
      return payload
    },
    typeText(type) {
      return type === 'M' ? 'Directory' : type === 'C' ? 'Menu' : 'Button'
    },
    typeTag(type) {
      return type === 'M' ? '' : type === 'C' ? 'success' : 'warning'
    },
    iconClass(row) {
      if (row.icon && row.icon.indexOf('el-icon') === 0) return row.icon
      if (row.menuType === 'M') return 'el-icon-folder-opened'
      if (row.menuType === 'C') return 'el-icon-menu'
      return 'el-icon-key'
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.menu-icon { margin-right: 8px; color: #606266; }
.danger { color: #f56c6c; }
::v-deep .el-input-number--mini { width: 96px; }
</style>
